/**
 * Отчёт загрузки книги ЭММ (ТЗ п. 6.2.9): сверка расчёта ОСНОВЫ с кэшем листа
 * «Расчет машино-час», модели книги против справочника техники, перечни лимитов.
 * Тот же отчёт — сразу после загрузки и в шапке версии из книги.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { PrcComparison, PrcExcelMismatch } from '@shared/api';
import { ScrollRegion, fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import { articleLabel } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { MODELS, MODELS_GEN, count, formatNumber } from './format';
import { NumTable, SectionNote, Stack, StatGrid } from './ui';

const VISIBLE_NAMES = 8;
const VISIBLE_ROWS = 25;

const Wrap = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const SubTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.base};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
`;

const Names = styled.span`
  overflow-wrap: anywhere;
`;

const Foot = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Названия моделей через запятую: первые восемь, остальные — по кнопке. */
function NameList({ names }: { names: readonly string[] }) {
  const [all, setAll] = useState(false);
  const id = useId();
  const shown = all ? names : names.slice(0, VISIBLE_NAMES);
  const rest = names.length - shown.length;
  return (
    <>
      <Names id={id}>{shown.join('; ')}</Names>
      {names.length > VISIBLE_NAMES && (
        <>
          {' '}
          <TextButton
            type="button"
            aria-expanded={all}
            aria-controls={id}
            onClick={() => setAll((v) => !v)}
          >
            {all ? 'свернуть' : `и ещё ${formatCount(rest)}`}
          </TextButton>
        </>
      )}
    </>
  );
}

function MismatchTable({ rows, total }: { rows: readonly PrcExcelMismatch[]; total: number }) {
  const [all, setAll] = useState(false);
  const tableId = useId();
  const shown = all ? rows : rows.slice(0, VISIBLE_ROWS);
  return (
    <div>
      <SubTitle>Расхождения с Excel больше 0,01 ₽</SubTitle>
      <ScrollRegion label="Расхождения расчёта ОСНОВЫ с Excel">
        <NumTable id={tableId}>
          <thead>
            <tr>
              <th scope="col">Модель</th>
              <th scope="col">Статья</th>
              <th scope="col" className="num">
                Год
              </th>
              <th scope="col" className="num">
                ОСНОВА, ₽
              </th>
              <th scope="col" className="num">
                Excel, ₽
              </th>
              <th scope="col" className="num">
                Разница, ₽
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m, i) =>
              'error' in m ? (
                <tr key={`${m.model}-${i}`}>
                  <td>{m.model}</td>
                  <td colSpan={5}>{typo(`Не посчитана: ${m.error}`)}</td>
                </tr>
              ) : (
                <tr key={`${m.model}-${m.article}-${m.year}-${i}`}>
                  <td>{m.model}</td>
                  <td>{articleLabel(m.article)}</td>
                  <td className="num">{m.year}</td>
                  <td className="num">{formatNumber(m.osnova, 4)}</td>
                  <td className="num">{formatNumber(m.excel, 4)}</td>
                  <td className="num strong">{formatNumber(m.diff, 4, 0, true)}</td>
                </tr>
              ),
            )}
          </tbody>
        </NumTable>
      </ScrollRegion>
      {rows.length > VISIBLE_ROWS && (
        <TextButton
          type="button"
          aria-expanded={all}
          aria-controls={tableId}
          onClick={() => setAll((v) => !v)}
        >
          {all ? 'Свернуть' : `Показать все ${formatCount(rows.length)}`}
        </TextButton>
      )}
      {total > rows.length && (
        <Foot>
          {typo(
            `Построчно хранятся первые ${formatCount(rows.length)} расхождений из ${formatCount(total)}.`,
          )}
        </Foot>
      )}
    </div>
  );
}

export function ComparisonReport({ comparison }: { comparison: PrcComparison }) {
  const registry = comparison.registry;
  const limits = comparison.limits;
  const missing = registry?.not_in_registry ?? [];
  const differs = registry?.passport_differs ?? [];
  const limitsMissing = limits?.not_in_registry ?? [];

  return (
    <Wrap>
      {comparison.recognized ? (
        <>
          <StatGrid $cols={4} aria-label="Сверка с Excel">
            <div>
              <dt>Сошлось с Excel до копейки</dt>
              <dd className="big">
                {formatCount(comparison.models_ok)} из {formatCount(comparison.models)}
                <span className="sub">{typo('моделей — по всем статьям и годам')}</span>
              </dd>
            </div>
            <div>
              <dt>Расхождений больше 0,01 ₽</dt>
              <dd className="big">{formatCount(comparison.mismatches_total)}</dd>
            </div>
            <div>
              <dt>Наибольшая разница</dt>
              <dd className="big">{formatNumber(comparison.max_abs_diff, 4)} ₽</dd>
            </div>
            <div>
              <dt>Проверено значений</dt>
              <dd className="big">{formatCount(comparison.checked)}</dd>
            </div>
          </StatGrid>
          {comparison.mismatches_total > 0 ? (
            <SectionNote>
              {typo(
                'Расхождение больше копейки значит, что в книге правили формулы. Утверждается расчёт ОСНОВЫ: значения Excel на веру не принимаются, но видны рядом для сверки.',
              )}
            </SectionNote>
          ) : (
            <SectionNote>
              {typo(
                'Расчёт ОСНОВЫ совпал с книгой: каждая статья каждого года и цена базового года — в пределах копейки.',
              )}
            </SectionNote>
          )}
        </>
      ) : (
        <Notice $tone="info">
          <span className="text">
            {typo(`Сверки с Excel нет: ${comparison.message}. Черновик посчитан формулами ОСНОВЫ.`)}
          </span>
        </Notice>
      )}

      <Stack>
        {missing.length > 0 && (
          <Notice $tone="warning">
            <span className="text">
              <strong>
                {typo(`В справочнике техники нет ${count(missing.length, MODELS_GEN)} книги`)}
              </strong>{' '}
              {typo(
                missing.length === 1
                  ? '— ПРЦ по ней не считается. Добавьте её загрузкой книги в «Технике»:'
                  : '— ПРЦ по ним не считается. Добавьте их загрузкой книги в «Технике»:',
              )}{' '}
              <NameList names={missing} />
            </span>
            <Link to="/agents/econ/fleet">Открыть «Технику»</Link>
          </Notice>
        )}
        {differs.length > 0 && (
          <Notice $tone="warning">
            <span className="text">
              <strong>
                {typo(
                  `У ${count(differs.length, MODELS_GEN)} паспорт в справочнике отличается от книги`,
                )}
              </strong>{' '}
              {typo(
                `в полях, от которых зависит ПРЦ: расчёт ОСНОВЫ по ${differs.length === 1 ? 'ней' : 'ним'} разойдётся с Excel. Паспорт обновляется загрузкой книги в «Технике», потом — кнопкой «Обновить паспорта» в версии:`,
              )}{' '}
              <NameList names={differs} />
            </span>
          </Notice>
        )}
        {limits && (
          <Notice $tone={limitsMissing.length > 0 ? 'warning' : 'info'}>
            <span className="text">
              {limits.models > 0
                ? typo(
                    `Перечни лимитов на ремонт найдены у ${count(limits.models, MODELS_GEN)} справочника — норма ремонта и ТО считается по перечню.`,
                  )
                : typo(
                    limits.message
                      ? `Перечней лимитов на ремонт нет: ${limits.message}`
                      : 'Перечней лимитов на ремонт в книге нет — норма ремонта берётся из паспорта.',
                  )}
              {limitsMissing.length > 0 && (
                <>
                  {' '}
                  {typo(
                    `Перечни моделей, которых нет в справочнике (${limitsMissing.length}):`,
                  )}{' '}
                  <NameList names={limitsMissing} />
                </>
              )}
            </span>
          </Notice>
        )}
      </Stack>

      {comparison.recognized && comparison.mismatches.length > 0 && (
        <MismatchTable rows={comparison.mismatches} total={comparison.mismatches_total} />
      )}

      {comparison.filename && (
        <Foot>
          Файл: {comparison.filename}
          {comparison.recognized ? ` · ${count(comparison.models, MODELS)} в листе расчёта` : ''}
        </Foot>
      )}
    </Wrap>
  );
}
